import { NavLink } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  Bell,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  ChevronRight,
  CircleDollarSign,
  Plus,
  WalletCards,
} from "lucide-react";
import { longDate, money, shortDate } from "@/util";

function StatCard({ label, value, change, tone, icon: Icon, testid }) {
  return (
    <div className={`stat-card ${tone}`} data-testid={testid}>
      <div className="stat-head">
        <span>{label}</span>
        <Icon size={18} />
      </div>
      <strong>{value}</strong>
      {change && (
        <small className={change.startsWith("-") ? "negative" : "positive"}>
          {change} <span>bu ay</span>
        </small>
      )}
    </div>
  );
}

export default function Dashboard({ data, user, onNew }) {
  const today = new Date();
  const chart = (data.flow || []).map((row) => ({
    name: new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "short" }).format(new Date(row.date)),
    income: Math.round(row.income / 1000),
    expense: Math.round(row.expense / 1000),
  }));
  const totalVolume = (data.flow || []).reduce((a, r) => a + r.income + r.expense, 0);
  const alerts = data.upcoming_tours || [];
  const payments = data.upcoming_payments || [];
  const collections = data.upcoming_collections || [];

  return (
    <>
      <section className="welcome">
        <div>
          <span className="eyebrow" data-testid="welcome-date">{longDate(today).toUpperCase()}</span>
          <h2 data-testid="welcome-title">
            Günaydın, {user?.name?.split(" ")[0] || "Yönetici"} <span>✦</span>
          </h2>
          <p>
            {alerts.length
              ? `Bugün ${alerts.length} tur yaklaşıyor, ${payments.length} ödeme bekliyor.`
              : "Yaklaşan 2 gün içinde planlanan tur yok, 👏"}
          </p>
        </div>
        <button className="outline-button" onClick={() => onNew("income")} data-testid="dashboard-new-income-button">
          <Plus size={16} /> Tahsilat ekle
        </button>
      </section>

      {alerts.length > 0 && (
        <div className="alert-row" data-testid="upcoming-tour-alerts">
          {alerts.map((tour) => (
            <div className="alert-strip prominent" key={tour.id} data-testid={`alert-${tour.id}`}>
              <Bell size={16} />
              <span>
                <b>{tour.days_left === 0 ? "Bugün" : `${tour.days_left} gün sonra`}:</b> {tour.name} · {tour.destination}
              </span>
              <NavLink to="/turlar" className="row-arrow-link">Detay <ChevronRight size={14} /></NavLink>
            </div>
          ))}
        </div>
      )}

      <div className="stats-grid">
        <StatCard tone="navy" icon={WalletCards} label="Toplam Nakit"
          value={money(data.cash || 0)} change={data.net_mtd >= 0 ? `+${money(data.net_mtd)}` : money(data.net_mtd)}
          testid="stat-cash" />
        <StatCard tone="orange" icon={CircleDollarSign} label="Bu Ay Gelir"
          value={money(data.income_mtd || 0)} testid="stat-income" />
        <StatCard tone="light" icon={BookOpen} label="Bu Ay Gider"
          value={money(data.expense_mtd || 0)} testid="stat-expense" />
        <StatCard tone="light" icon={BriefcaseBusiness} label="Aktif Turlar"
          value={String(data.tour_count || 0)} change={`${alerts.length} yaklaşan`} testid="stat-active-tours" />
      </div>

      <div className="dashboard-grid">
        <section className="panel chart-panel" data-testid="cashflow-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">NAKİT AKIŞI</span>
              <h3>Son 14 gün hareketi</h3>
            </div>
            <div className="chart-legend small">
              <span><i className="dot income" /> Gelir</span>
              <span><i className="dot expense" /> Gider</span>
            </div>
          </div>
          <div className="chart-total">
            <b>{money(totalVolume)}</b>
            <small>toplam hacim</small>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0f4c5c" stopOpacity=".22" />
                  <stop offset="100%" stopColor="#0f4c5c" stopOpacity="0" />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#8794a5", fontSize: 11 }} interval={1} />
              <YAxis hide />
              <Tooltip formatter={(v) => [`₺ ${v} bin`, ""]} />
              <Area type="monotone" dataKey="income" stroke="#0f4c5c" fill="url(#incomeFill)" strokeWidth={3} />
              <Area type="monotone" dataKey="expense" stroke="#e36414" fill="none" strokeWidth={2} strokeDasharray="5 5" />
            </AreaChart>
          </ResponsiveContainer>
        </section>

        <section className="panel upcoming" data-testid="upcoming-tours-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">OPERASYON TAKVİMİ</span>
              <h3>Yaklaşan turlar</h3>
            </div>
            <NavLink to="/turlar" data-testid="view-all-tours-link">Tümünü gör <ChevronRight size={15} /></NavLink>
          </div>
          <div className="tour-list">
            {(data.tours || []).slice(0, 4).map((tour, index) => (
              <div className="tour-row" key={tour.id} data-testid={`upcoming-tour-${index}`}>
                <div className="date-tile">
                  <b>{shortDate(tour.start_date).split(" ")[0]}</b>
                  <small>{shortDate(tour.start_date).split(" ")[1]}</small>
                </div>
                <div className="tour-info">
                  <b>{tour.name}</b>
                  <small>{tour.destination}</small>
                </div>
                <div className="tour-progress">
                  <b>{tour.sold}/{tour.capacity}</b>
                  <div><span style={{ width: `${(tour.sold / tour.capacity) * 100}%` }} /></div>
                  <small>katılımcı</small>
                </div>
                <ChevronRight size={16} className="row-arrow" />
              </div>
            ))}
            {!data.tours?.length && <p className="empty-inline">Henüz tur yok. Üstteki "Yeni Kayıt" ile ekle.</p>}
          </div>
        </section>
      </div>

      <div className="dashboard-grid secondary">
        <section className="panel" data-testid="pending-payments-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">BEKLEYEN ÖDEMELER</span>
              <h3>Yaklaşan giderler</h3>
            </div>
            <NavLink to="/finans" data-testid="view-all-payments-link">Finansa git <ChevronRight size={15} /></NavLink>
          </div>
          <div className="ledger-list">
            {payments.length ? payments.map((p) => (
              <div className="ledger-row" key={p.id} data-testid={`payment-${p.id}`}>
                <div className="ledger-icon expense"><BookOpen size={14} /></div>
                <div className="ledger-info">
                  <b>{p.title}</b>
                  <small>{p.category} · {p.account}</small>
                </div>
                <div className="ledger-amount expense">
                  <b>-{money(p.amount)}</b>
                  <small>{shortDate(p.date)}</small>
                </div>
              </div>
            )) : <p className="empty-inline">Bekleyen ödeme yok.</p>}
          </div>
        </section>

        <section className="panel" data-testid="pending-collections-panel">
          <div className="panel-title">
            <div>
              <span className="eyebrow">YAKLAŞAN TAHSİLATLAR</span>
              <h3>Alınacak tutarlar</h3>
            </div>
            <NavLink to="/finans" data-testid="view-all-collections-link">Finansa git <ChevronRight size={15} /></NavLink>
          </div>
          <div className="ledger-list">
            {collections.length ? collections.map((p) => (
              <div className="ledger-row" key={p.id} data-testid={`collection-${p.id}`}>
                <div className="ledger-icon income"><CircleDollarSign size={14} /></div>
                <div className="ledger-info">
                  <b>{p.title}</b>
                  <small>{p.category} · {p.account}</small>
                </div>
                <div className="ledger-amount income">
                  <b>+{money(p.amount)}</b>
                  <small>{shortDate(p.date)}</small>
                </div>
              </div>
            )) : <p className="empty-inline">Bekleyen tahsilat yok.</p>}
          </div>
        </section>
      </div>

      <section className="panel quick-panel" data-testid="quick-actions-panel">
        <div className="panel-title">
          <div>
            <span className="eyebrow">HIZLI ERİŞİM</span>
            <h3>Bugün ne yapmak istersin?</h3>
          </div>
        </div>
        <div className="quick-actions">
          <button onClick={() => onNew("income")} data-testid="quick-add-income">
            <CircleDollarSign />
            <span>Gelir ekle<small>Yeni tahsilat kaydı</small></span>
            <Plus />
          </button>
          <button onClick={() => onNew("expense")} data-testid="quick-add-expense">
            <BookOpen />
            <span>Gider ekle<small>Masraf kaydı oluştur</small></span>
            <Plus />
          </button>
          <button onClick={() => onNew("tour")} data-testid="quick-add-tour">
            <CalendarDays />
            <span>Tur oluştur<small>Yeni operasyon planla</small></span>
            <Plus />
          </button>
        </div>
      </section>
    </>
  );
}
